import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const pillars = [
  {
    icon: "ri-film-line",
    title: "作品档案",
    text: "按年份与类型整理电影、电视剧作品，保留导演、主演、时长与上映信息，让每一部作品都能被准确检索。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯速递",
    text: "收录影评、行业新闻、活动报道与演员动态，按时间倒序更新，重要节点会单独做成焦点专题。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "影迷收藏",
    text: "喜欢的作品与文章可以一键收藏，保存在本机浏览器中，最多 50 条，随时回来接着看。",
  },
];

export default function About() {
  useDocumentMeta(
    "关于吴梦梦 - 吴梦梦影视官网",
    "了解吴梦梦影视官网的定位、内容范围与联系方式。我们以作品档案与资讯记录为核心，为影迷提供清晰可靠的影视信息入口。",
    "关于吴梦梦,吴梦梦影视官网,品牌介绍,联系方式",
  );

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20empty%20film%20set%20at%20night%20with%20a%20single%20warm%20orange%20spotlight%20beam%20cutting%20through%20deep%20navy%20blue%20darkness%2C%20haze%20and%20dust%20in%20the%20air%2C%20soft%20film%20grain%2C%20minimal%20editorial%20composition%2C%20no%20text%2C%20no%20watermark&width=1600&height=700&seq=about-hero&orientation=landscape"
          alt="影视片场氛围背景"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/82 to-primary-950/55" />

        <div className="relative container-content py-9 md:py-16">
          <Breadcrumb tone="onDark" items={[{ label: "首页", to: "/" }, { label: "关于" }]} />
          <div className="mt-6 md:mt-8 max-w-2xl">
            <span className="text-[10px] md:text-[11px] font-label tracking-[0.24em] text-accent-300 uppercase">
              About the Brand
            </span>
            <h1 className="mt-3 text-3xl md:text-5xl font-black text-background-50 tracking-tight">
              关于吴梦梦
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-200/85 leading-relaxed">
              这是一个为演员吴梦梦建立的影视信息门户。我们把散落在不同平台的作品信息、影评与动态整理到同一个地方，
              让想了解她的人不必再一页一页地翻找。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/works"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                浏览作品档案
                <i className="ri-arrow-right-line text-base leading-none" />
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/35 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                查看最新资讯
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-content py-10 md:py-16">
        <div className="max-w-3xl">
          <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
            Our Purpose
          </span>
          <h2 className="mt-2 text-xl md:text-[28px] font-bold leading-snug">网站定位与宗旨</h2>
          <p className="mt-4 text-sm md:text-[15px] text-foreground-600 leading-[1.9]">
            吴梦梦影视官网以「作品」与「记录」为核心。作品档案提供可检索的结构化信息，资讯部分则保留作品诞生过程中的细节：
            一次开机的现场、一段映后的对话、一篇认真写下的影评。我们希望这个站点既是一个索引，也是一份持续生长的档案。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {pillars.map((pillar) => (
            <div key={pillar.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${pillar.icon} text-xl leading-none`} />
              </span>
              <h3 className="mt-4 text-base font-semibold">{pillar.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{pillar.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 border-y border-background-200">
        <div className="container-content py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-xl md:text-[26px] font-bold">内容说明</h2>
              <div className="mt-4 flex flex-col gap-4 text-sm text-foreground-600 leading-[1.9]">
                <p>
                  本站全部作品、资讯、动态与人物言论均为演示示例内容，用于展示网站的信息结构与阅读体验，
                  不对应任何真实项目、真实票房或真实采访。
                </p>
                <p>
                  作品评分、热度指数与收藏数量为界面演示数据；收藏功能使用浏览器本地存储实现，不会上传到服务器，
                  清理浏览器数据后收藏记录会一并消失。
                </p>
                <p>
                  站内搜索与筛选均为前端模拟逻辑，匹配范围为本站示例数据，未接入任何外部数据接口。
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <i className="ri-contacts-line text-accent-500 leading-none" />
                联系方式
              </h2>
              <ul className="mt-4 flex flex-col divide-y divide-background-200">
                <li className="flex items-start gap-3 py-3 first:pt-0">
                  <i className="ri-mail-line text-base leading-none text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-foreground-500">合作与媒体联络</p>
                    <p className="mt-1 text-sm text-foreground-900 font-label">contact@wumengmeng.film</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 py-3">
                  <i className="ri-weibo-line text-base leading-none text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-foreground-500">官方社媒</p>
                    <p className="mt-1 text-sm text-foreground-900">微博 / 微信 / Instagram / YouTube</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 py-3">
                  <i className="ri-time-line text-base leading-none text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-foreground-500">回复时间</p>
                    <p className="mt-1 text-sm text-foreground-900">工作日 10:00 - 18:00</p>
                  </div>
                </li>
              </ul>
              <p className="mt-4 text-[11px] text-foreground-500 leading-relaxed">
                以上联系方式与社媒账号为演示信息，不用于真实联络。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-content py-10 md:py-14">
        <h2 className="text-xl md:text-[26px] font-bold">版权声明与使用说明</h2>
        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-5 py-5 md:px-6 md:py-6">
          <div className="flex flex-col gap-4 text-sm text-foreground-600 leading-[1.9]">
            <p>
              本站内容（含文字、排版与示例数据）仅用于产品演示。站内图片为生成式演示素材，不指向任何真实人物肖像，
              如需用于商业用途请另行取得授权。
            </p>
            <p>
              欢迎引用站内作品资料并注明来源，但请勿将演示数据作为真实影视信息对外发布。
              如发现内容存在明显错误，可通过上方邮箱反馈（演示渠道）。
            </p>
            <p className="text-foreground-500 text-xs">© 2026 吴梦梦影视官网 · 保留所有权利</p>
          </div>
        </div>
      </section>
    </div>
  );
}